import { useCallback, useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import { useLogoutMutation } from 'auth/hooks/loginApi';
import { usePosthogCapture } from 'auth/posthogCapture/_index';
import {
  INACTIVITY_TIMEOUT_MS,
  SHARED_DEVICE_KEY,
  readSharedDeviceRecord,
  writeSharedDeviceRecord,
} from 'auth/helpers/sharedDeviceStorage';
import { SharedDeviceDialogStep } from 'auth/components/SharedDeviceLockoutDialog';

const ACTIVITY_WRITE_THROTTLE_MS = 10_000;
const IDLE_CHECK_INTERVAL_MS = 60_000;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'keydown',
  'click',
  'touchstart',
  'scroll',
];

interface UseSharedDeviceLockoutResult {
  step: SharedDeviceDialogStep;
  onAnswerShared: () => void;
  onAnswerNotShared: () => void;
  onSwitchAccounts: () => void;
  onClose: () => void;
}

export default function useSharedDeviceLockout(
  userId: string | undefined
): UseSharedDeviceLockoutResult {
  const [step, setStep] = useState<SharedDeviceDialogStep>('closed');
  const { mutate: logout } = useLogoutMutation();
  const posthog = usePostHog();
  const auth = usePosthogCapture();

  useEffect(() => {
    if (!userId) return;
    const now = Date.now();
    const record = readSharedDeviceRecord();

    if (!record) {
      writeSharedDeviceRecord({
        status: 'unknown',
        userId,
        lastActivityAt: now,
      });
      return;
    }

    if (record.status === 'not_shared' && record.userId !== userId) {
      writeSharedDeviceRecord({
        status: 'unknown',
        userId,
        lastActivityAt: now,
      });
      return;
    }

    writeSharedDeviceRecord({ ...record, userId, lastActivityAt: now });
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;

    let lastWrite = 0;

    const handleActivity = (): void => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
      lastWrite = now;
      const record = readSharedDeviceRecord();
      if (!record) return;
      writeSharedDeviceRecord({ ...record, lastActivityAt: now });
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;

    const check = (): void => {
      const record = readSharedDeviceRecord();
      if (!record) return;
      const idleMs = Date.now() - record.lastActivityAt;
      if (idleMs < INACTIVITY_TIMEOUT_MS) return;

      if (record.status === 'shared') {
        auth.inactivityLoggedOut();
        logout();
        posthog?.reset();
        return;
      }

      if (record.status === 'unknown') {
        setStep((prev) => {
          if (prev !== 'closed') return prev;
          auth.sharedDevicePromptShown();
          return 'ask_shared';
        });
      }
    };

    check();
    const interval = window.setInterval(check, IDLE_CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [userId, logout, posthog, auth]);

  useEffect(() => {
    const handler = (event: StorageEvent): void => {
      if (event.key !== SHARED_DEVICE_KEY) return;
      const record = readSharedDeviceRecord();
      if (record && record.status !== 'unknown') {
        setStep((prev) => (prev === 'ask_shared' ? 'closed' : prev));
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const onAnswerShared = useCallback((): void => {
    if (!userId) return;
    writeSharedDeviceRecord({
      status: 'shared',
      userId,
      lastActivityAt: Date.now(),
    });
    auth.sharedDevicePromptAnswered({ answer: 'yes' });
    setStep('switch_accounts');
  }, [userId, auth]);

  const onAnswerNotShared = useCallback((): void => {
    if (!userId) return;
    writeSharedDeviceRecord({
      status: 'not_shared',
      userId,
      lastActivityAt: Date.now(),
    });
    auth.sharedDevicePromptAnswered({ answer: 'no' });
    setStep('closed');
  }, [userId, auth]);

  const onSwitchAccounts = useCallback((): void => {
    auth.sharedDeviceSwitchAccountClicked();
    logout();
    posthog?.reset();
  }, [logout, posthog, auth]);

  const onClose = useCallback((): void => {
    const record = readSharedDeviceRecord();
    if (record) {
      writeSharedDeviceRecord({ ...record, lastActivityAt: Date.now() });
    }
    setStep('closed');
  }, []);

  return { step, onAnswerShared, onAnswerNotShared, onSwitchAccounts, onClose };
}
